import { connect, FormikValues } from 'formik'
import { cloneDeep, flatten, get, pullAt } from 'lodash'
import { Fragment, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { Text } from '@/components/Form'
import { MentionsRichTextEditorWithFormik } from '@/components/Form/MentionsRichTextEditor/MentionsRichTextEditor'
import { Delete } from '@/components/Icons'
import { Row } from '@/components/StyledComponents'
import {
  StyledDivider,
  StyledWandIcon,
  VariableButton,
  VariableInputContainer
} from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import operationsMessages from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/messages'
import { FieldTypes, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import { AddButton } from './ButtonBlock'
import messages from './messages'
import { AnyContentBlockTypes } from './NotificationDetail'

const StyledRow = styled(Row)`
  padding-right: 10px;
`

const DeleteActionContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 17px;
`

const StyledText = styled(Text)`
  width: 100%;
`

interface FactTypes {
  type: string
  title: string
  value: string
}

const emptyFact = { type: 'Fact', title: '', value: '' }

const initialData = {
  facts: [{ ...emptyFact }],
  type: 'FactSet'
}

interface FactSetProps {
  name: string
  data: any
  onChange: () => {}
  variables: Variable[][]
  readOnly?: boolean
}

const Component = connect(({ name, variables, formik, readOnly = false }: FactSetProps & FormikValues) => {
  const intl = useIntl()

  const flattenedVariables = flatten(variables)
  const addVariableRef = useRef<any>(() => {})
  const [showValueVariablePopOver, setShowValueVariablePopOver] = useState<any>()
  const factSet = get(formik.values, name, { ...initialData })

  const addLabel = () => {
    const newFact = emptyFact
    const facts = get(formik.values, `${name}.facts`, [])
    formik.setFieldValue(`${name}.facts`, [...facts, newFact])
  }

  const removeLabel = (index: number) => {
    const facts = get(formik.values, `${name}.facts`, [])
    const resultArray = cloneDeep(facts)
    pullAt(resultArray, [index])
    formik.setFieldValue(`${name}.facts`, resultArray)
  }

  return (
    <>
      {factSet.facts.map((fact: FactTypes, index: number) => (
        <Fragment key={index}>
          <StyledDivider />
          <VariableInputContainer>
            <StyledRow>
              <StyledText
                disabled={readOnly}
                aria-label={intl.formatMessage(messages.labelTitle)}
                name={`${name}.facts[${index}].title`}
                placeholder={intl.formatMessage(messages.labelTitle)}
                required
                spellCheck
                label={intl.formatMessage(messages.labelTitle)}
              />
              {(index !== 0 || factSet.facts.length > 1) && !readOnly && (
                <DeleteActionContainer className="icon remove" onClick={() => removeLabel(index)}>
                  <Delete title="Delete Label" />
                </DeleteActionContainer>
              )}
            </StyledRow>
            <StyledRow>
              <MentionsRichTextEditorWithFormik
                readOnly={readOnly}
                setAddMention={addMentionFn => (addVariableRef.current[`${name}.facts[${index}].value`] = addMentionFn)}
                name={`${name}.facts[${index}].value`}
                mentions={getAllFlattenedVariables(flattenedVariables)}
                placeholder={intl.formatMessage(messages.value)}
                label={intl.formatMessage(messages.value)}
                required={true}
              />
              {flattenedVariables.length > 0 && !readOnly && (
                <VariableButton
                  onClick={() => {
                    setShowValueVariablePopOver(index)
                  }}
                >
                  <StyledWandIcon title={intl.formatMessage(operationsMessages.addVariableButton)} />
                </VariableButton>
              )}
            </StyledRow>
            {showValueVariablePopOver === index && (
              <VariableOverlay
                onSelectVariable={(variable: Variable) =>
                  addVariableRef.current[`${name}.facts[${index}].value`](variable)
                }
                onClose={() => {
                  setShowValueVariablePopOver(undefined)
                }}
                variables={variables}
                validVariableTypes={[
                  FieldTypes.STRING,
                  FieldTypes.BOOLEAN,
                  FieldTypes.INTEGER,
                  FieldTypes.NUMBER,
                  FieldTypes.ANY
                ]}
              />
            )}
          </VariableInputContainer>
        </Fragment>
      ))}
      {!readOnly && <AddButton onClick={addLabel} message={'FactSet'} />}
    </>
  )
})

const FactSet: AnyContentBlockTypes = { Component }

FactSet.displayName = 'FactSet'

FactSet.initialData = { ...initialData }

export default FactSet
